import type { FoyerCommand, RealtimeEvent } from "@shared/types/foyer";

/**
 * TV-side helpers for the durable-command channel (Phase 2).
 *
 * Web PubSub delivers a command's realtime event live; these helpers close the
 * reliability gap around it: acknowledging what was executed (the server's only
 * proof of delivery) and draining anything missed while the TV was offline.
 *
 * All calls are screen-token-gated and best-effort - a failed ack is harmless
 * because the server's sweep re-publishes un-acked commands until they land.
 */

interface AuthArgs {
  screenId: string;
  screenToken: string;
  apiBase?: string;
}

/** Confirm a command was executed. `keepalive` lets the ack survive a
 *  reboot-triggered page reload that fires immediately after. */
export async function ackCommand(
  args: AuthArgs & { commandId: string; result?: Record<string, unknown> }
): Promise<void> {
  try {
    await fetch(
      `${args.apiBase ?? ""}/api/common/v1/screens/${args.screenId}/commands/${args.commandId}/ack`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "X-Screen-Token": args.screenToken,
        },
        body: JSON.stringify(args.result ? { result: args.result } : {}),
        keepalive: true,
      }
    );
  } catch {
    // Best-effort: the server sweep re-publishes un-acked commands.
  }
}

/** Fetch open commands the device may have missed (reconnect drain). */
export async function fetchPendingCommands(
  args: AuthArgs
): Promise<FoyerCommand[]> {
  try {
    const res = await fetch(
      `${args.apiBase ?? ""}/api/common/v1/screens/${args.screenId}/commands/pending`,
      { headers: { "X-Screen-Token": args.screenToken } }
    );
    if (!res.ok) return [];
    const body = (await res.json()) as { commands?: FoyerCommand[] };
    return body.commands ?? [];
  } catch {
    return [];
  }
}

/**
 * Map a drained command back to the realtime event the TV's event handler
 * already knows how to apply - so the offline-replay path reuses the exact
 * same execution logic as the live path. Mirrors the server's
 * `buildRealtimeEvent`. Returns null for command kinds the TV can't act on.
 */
export function commandToEvent(cmd: FoyerCommand): RealtimeEvent | null {
  switch (cmd.type) {
    case "reboot":
      return {
        type: "reboot",
        premiseId: cmd.premiseId,
        screenId: cmd.screenId,
        commandId: cmd.commandId,
      };
    case "unpair":
      return {
        type: "unpair",
        premiseId: cmd.premiseId,
        screenId: cmd.screenId,
        commandId: cmd.commandId,
      };
    case "refresh_telemetry":
      return {
        type: "refresh_telemetry",
        premiseId: cmd.premiseId,
        screenId: cmd.screenId,
        commandId: cmd.commandId,
      };
    case "screenshot":
      return {
        type: "screenshot",
        premiseId: cmd.premiseId,
        screenId: cmd.screenId,
        commandId: cmd.commandId,
      };
    case "log_capture":
      return {
        type: "log_capture",
        premiseId: cmd.premiseId,
        screenId: cmd.screenId,
        commandId: cmd.commandId,
      };
    case "ota_apply": {
      const p = cmd.payload ?? {};
      return {
        type: "ota_apply",
        premiseId: cmd.premiseId,
        screenId: cmd.screenId,
        kind: p.kind === "apk" || p.kind === "firmware" ? p.kind : "web_bundle",
        targetVersion:
          typeof p.targetVersion === "string" ? p.targetVersion : "",
        url: typeof p.url === "string" ? p.url : undefined,
        fileName: typeof p.fileName === "string" ? p.fileName : undefined,
        sizeBytes: typeof p.sizeBytes === "number" ? p.sizeBytes : undefined,
        commandId: cmd.commandId,
      };
    }
    case "clone_restore": {
      const p = cmd.payload ?? {};
      return {
        type: "clone_restore",
        premiseId: cmd.premiseId,
        screenId: cmd.screenId,
        url: typeof p.url === "string" ? p.url : "",
        folder: typeof p.folder === "string" ? p.folder : "",
        commandId: cmd.commandId,
      };
    }
    case "set_ntp": {
      const p = cmd.payload ?? {};
      return {
        type: "set_ntp",
        premiseId: cmd.premiseId,
        screenId: cmd.screenId,
        ntpServer: typeof p.ntpServer === "string" ? p.ntpServer : "",
        commandId: cmd.commandId,
      };
    }
    case "power": {
      const p = cmd.payload ?? {};
      return {
        type: "power",
        premiseId: cmd.premiseId,
        screenId: cmd.screenId,
        on: p.on === true,
        commandId: cmd.commandId,
      };
    }
    case "launch_app": {
      const p = cmd.payload ?? {};
      return {
        type: "launch_app",
        premiseId: cmd.premiseId,
        screenId: cmd.screenId,
        appId: typeof p.appId === "string" ? p.appId : undefined,
        name: typeof p.name === "string" ? p.name : undefined,
        androidSlug:
          typeof p.androidSlug === "string" ? p.androidSlug : undefined,
        tizenId: typeof p.tizenId === "string" ? p.tizenId : undefined,
        webosId: typeof p.webosId === "string" ? p.webosId : undefined,
        iosScheme: typeof p.iosScheme === "string" ? p.iosScheme : undefined,
        webUrl: typeof p.webUrl === "string" ? p.webUrl : undefined,
        commandId: cmd.commandId,
      };
    }
    case "checkin_signal": {
      const p = cmd.payload ?? {};
      return {
        type: "checkin_signal",
        premiseId: cmd.premiseId,
        screenId: cmd.screenId,
        roomId: typeof p.roomId === "string" ? p.roomId : "",
        commandId: cmd.commandId,
      };
    }
    case "checkout_wipe": {
      const p = cmd.payload ?? {};
      return {
        type: "checkout_wipe",
        premiseId: cmd.premiseId,
        screenId: cmd.screenId,
        roomId: typeof p.roomId === "string" ? p.roomId : "",
        commandId: cmd.commandId,
      };
    }
    case "show_message": {
      const p = cmd.payload ?? {};
      return {
        type: "show_message",
        premiseId: cmd.premiseId,
        roomId: typeof p.roomId === "string" ? p.roomId : "",
        title: typeof p.title === "string" ? p.title : undefined,
        body: typeof p.body === "string" ? p.body : "",
        durationMs: typeof p.durationMs === "number" ? p.durationMs : 8000,
        commandId: cmd.commandId,
      };
    }
    default:
      return null;
  }
}
